import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Minus, Plus } from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { showApiError } from '@/lib/utils/notifications'
import { cn } from '@/lib/utils/helpers'
import { formatChangeLogCreatedAt } from '@/lib/utils/formatChangeLogCreatedAt'
import { roleFormSchema, type RoleFormValues } from '../types/schemas'
import type {
  PolicyByCategory,
  RoleChangeLog,
  TileSummary,
  RoleEditModalProps,
} from '../types'
import { getAllPolicy, getAllTiles, getRole } from '../services/rolesService'
import { asStringIdArray } from '../utils/asStringIdArray'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'

/** Liberty / Angular `custom-form-control` — shared field look */
const L = {
  label: 'block text-sm font-medium text-[#232946] mb-1',
  control:
    'w-full min-h-[38px] rounded border border-[#ced4da] bg-white px-3 py-2 text-sm text-[#232946] shadow-sm focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-1 focus:ring-[rgb(21_151_255)]',
  controlReadonly:
    'w-full min-h-[38px] rounded border border-[#ced4da] bg-[#e9ecef] px-3 py-2 text-sm text-[#232946] focus:outline-none',
  textarea: 'w-full min-h-[120px] rounded border border-[#ced4da] bg-white px-3 py-2 text-sm text-[#232946] shadow-sm focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-1 focus:ring-[rgb(21_151_255)]',
  sectionLabel: 'block text-sm font-medium text-[#232946] mt-1 mb-2',
  check: 'mt-0.5 h-4 w-4 shrink-0 rounded border-[#ced4da] text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]',
  primaryBtn:
    'inline-flex min-h-[38px] items-center justify-center rounded px-4 text-sm font-medium text-white bg-[rgb(21_151_255)] hover:bg-[#0d7acc] disabled:bg-slate-300 disabled:cursor-not-allowed shadow-sm',
}

/** Angular `col-lg-5` tile row: checkbox + label, word-break. */
function TileCheckRow({
  id,
  label,
  checked,
  onChange,
  disabled,
}: {
  id: string
  label: string
  checked: boolean
  onChange: () => void
  disabled: boolean
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 max-w-full flex-[1_1_41%] flex-col sm:max-w-[calc(50%-0.5rem)]',
        disabled && 'opacity-60',
      )}
    >
      <div className="form-group flex items-start gap-2">
        <input
          id={`tile-${id}`}
          type="checkbox"
          className={L.check}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
        />
        <label
          htmlFor={`tile-${id}`}
          className="min-w-0 flex-1 text-sm leading-snug text-[#232946] [word-break:break-word]"
        >
          {label}
        </label>
      </div>
    </div>
  )
}

export function RoleEditModal({
  isOpen,
  roleKey,
  allRoles,
  catalogTiles,
  catalogPolicyByCategory,
  submitting,
  onClose,
  onSubmit,
}: RoleEditModalProps) {
  const [roleLoadPending, setRoleLoadPending] = useState(false)
  const [resolvedTiles, setResolvedTiles] = useState<TileSummary[]>([])
  const [resolvedPolicy, setResolvedPolicy] = useState<PolicyByCategory>({})
  const [checkedTiles, setCheckedTiles] = useState<Set<string>>(new Set())
  const [checkedPolicies, setCheckedPolicies] = useState<Set<string>>(new Set())
  const [changeLog, setChangeLog] = useState<RoleChangeLog[]>([])
  const [applyFromKey, setApplyFromKey] = useState('')
  /** One expanded policy group at a time — matches Angular `selectedGroup`. */
  const [expandedPolicy, setExpandedPolicy] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isValid },
  } = useForm<RoleFormValues>({
    resolver: zodResolver(roleFormSchema),
    mode: 'onChange',
    defaultValues: { role_name: '', role_description: '', report_url: '' },
  })

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    if (catalogTiles.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: resolve tiles from the catalog or fetch them when the modal opens
      setResolvedTiles(catalogTiles)
    } else {
      void getAllTiles()
        .then((list) => {
          if (!cancelled) setResolvedTiles(list)
        })
        .catch((err) => {
          if (!cancelled) showApiError(err)
        })
    }
    if (Object.keys(catalogPolicyByCategory).length > 0) {
      setResolvedPolicy(catalogPolicyByCategory)
    } else {
      void getAllPolicy()
        .then((p) => {
          if (!cancelled) setResolvedPolicy(p)
        })
        .catch((err) => {
          if (!cancelled) showApiError(err)
        })
    }
    return () => {
      cancelled = true
    }
  }, [isOpen, catalogTiles, catalogPolicyByCategory])

  useEffect(() => {
    if (!isOpen || !roleKey) return
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: loading state for the role fetch when the modal opens (data-load effect)
    setRoleLoadPending(true)
    setApplyFromKey('')

    getRole(roleKey)
      .then((role) => {
        if (cancelled) return
        if (role) {
          reset({
            role_name: role.role_name ?? '',
            role_description: role.role_description ?? '',
            report_url: role.report_url ?? '',
          })
          setCheckedTiles(new Set(asStringIdArray(role.tiles)))
          setCheckedPolicies(new Set(asStringIdArray(role.policy)))
          setChangeLog(role.notes ?? [])
          void trigger()
        }
      })
      .catch((err) => {
        if (!cancelled) showApiError(err)
      })
      .finally(() => {
        if (!cancelled) setRoleLoadPending(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, roleKey, reset, trigger])

  const formLocked = roleLoadPending

  const toggleTile = (tileId: string) => {
    if (formLocked) return
    setCheckedTiles((prev) => {
      const next = new Set(prev)
      if (next.has(tileId)) next.delete(tileId)
      else next.add(tileId)
      return next
    })
  }

  const togglePolicy = (policyKey: string) => {
    if (formLocked) return
    setCheckedPolicies((prev) => {
      const next = new Set(prev)
      if (next.has(policyKey)) next.delete(policyKey)
      else next.add(policyKey)
      return next
    })
  }

  const toggleCategory = (category: string, checked: boolean) => {
    if (formLocked) return
    setCheckedPolicies((prev) => {
      const next = new Set(prev)
      const items = resolvedPolicy[category] ?? []
      items.forEach((item) => {
        const key = String(item.policy_key)
        if (checked) next.add(key)
        else next.delete(key)
      })
      return next
    })
  }

  const applyFromRole = async (sourceRoleKey: string) => {
    if (!sourceRoleKey) return
    try {
      const source = await getRole(sourceRoleKey)
      setCheckedPolicies(new Set(asStringIdArray(source.policy)))
    } catch (error) {
      showApiError(error)
    }
  }

  const internalSubmit = (values: RoleFormValues) => {
    if (!roleKey) return
    onSubmit(roleKey, {
      role_name: values.role_name.trim(),
      role_description: values.role_description.trim(),
      report_url: values.report_url.trim() || undefined,
      tiles: Array.from(checkedTiles),
      policy: Array.from(checkedPolicies),
    })
  }

  const categories = useMemo(
    () => Object.keys(resolvedPolicy).sort(),
    [resolvedPolicy],
  )
  const otherRoles = useMemo(
    () => allRoles.filter((r) => r.role_key && r.role_key !== roleKey),
    [allRoles, roleKey],
  )

  const togglePolicyGroup = (key: string) => {
    setExpandedPolicy((prev) => (prev === key ? null : key))
  }

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Edit Role"
      onClose={onClose}
      maxWidth="max-w-800"
      headerVariant="centeredTitle"
      bodyScrollOnly
      bodyClassName="!pt-3"
      panelDataTestId="role-edit-modal-panel"
      footer={
        <div
          className="flex flex-wrap items-center justify-center gap-3"
          data-testid="role-edit-modal-footer"
        >
          <button
            type="submit"
            form="role-edit-form"
            disabled={submitting || !isValid || formLocked}
            className={cn(primaryEntityActionButtonClass, 'min-w-[120px]')}
            data-testid="role-edit-submit"
          >
            {submitting ? 'Saving...' : 'Submit'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={cn(secondaryEntityActionButtonClass, 'min-w-[120px]')}
            data-testid="role-edit-cancel"
          >
            Cancel
          </button>
        </div>
      }
    >
      <form
        id="role-edit-form"
        onSubmit={handleSubmit(internalSubmit)}
        className="form_wrap text-left text-[#232946] [&>div+div]:mt-3"
        aria-busy={formLocked}
        noValidate
      >
        <div className="w-full">
          <label className={L.label} htmlFor="role_name_field">
            Role Name
          </label>
          <input
            id="role_name_field"
            type="text"
            readOnly
            disabled={formLocked}
            className={L.controlReadonly}
            {...register('role_name')}
          />
          {errors.role_name && (
            <p className="text-xs text-red-600 mt-1">{errors.role_name.message}</p>
          )}
        </div>

        <div className="w-full">
          <label className={L.label} htmlFor="role_description_field">
            Role Description
          </label>
          <textarea
            id="role_description_field"
            rows={5}
            disabled={formLocked}
            className={L.textarea}
            {...register('role_description')}
          />
          {errors.role_description && (
            <p className="text-xs text-red-600 mt-1">{errors.role_description.message}</p>
          )}
        </div>

        <div className="w-full">
          <label className={L.label} htmlFor="report_url_field">
            Report Url
          </label>
          <input
            id="report_url_field"
            type="text"
            disabled={formLocked}
            className={L.control}
            {...register('report_url')}
          />
        </div>

        <div className="w-full">
          <span className={L.sectionLabel}>Dashboard Tiles</span>
          {resolvedTiles.length === 0 ? (
            <p className="text-sm text-slate-500">No tiles defined.</p>
          ) : (
            <div className="flex flex-wrap gap-x-6 gap-y-2.5">
              {resolvedTiles.map((tile) => {
                const id = String(tile.tile_id)
                return (
                  <TileCheckRow
                    key={id}
                    id={id}
                    label={tile.tile_name ?? `Tile ${id}`}
                    checked={checkedTiles.has(id)}
                    onChange={() => toggleTile(id)}
                    disabled={formLocked}
                  />
                )
              })}
            </div>
          )}
        </div>

        <div className="w-full">
          <span className="mt-3 block text-sm font-medium text-[#232946]">Role Policy</span>

          {otherRoles.length > 0 && (
            <div className="ml-0 mt-2 flex flex-col gap-3 sm:ml-3 sm:flex-row sm:items-end sm:justify-end">
              <div className="min-w-0 sm:ml-auto sm:w-[38%] sm:max-w-[14rem]">
                <label className={L.label} htmlFor="apply_from_role">
                  Apply From Role
                </label>
                <select
                  id="apply_from_role"
                  value={applyFromKey}
                  disabled={formLocked}
                  onChange={(e) => setApplyFromKey(e.target.value)}
                  className={L.control}
                >
                  <option value="">Select Role</option>
                  {otherRoles.map((r) => (
                    <option key={r.role_key} value={r.role_key}>
                      {r.role_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="shrink-0 sm:pt-0">
                <button
                  type="button"
                  disabled={formLocked || !applyFromKey}
                  onClick={() => {
                    if (applyFromKey) void applyFromRole(applyFromKey)
                  }}
                  className={L.primaryBtn}
                >
                  Apply
                </button>
              </div>
            </div>
          )}

          <div className="ml-0 mt-3 space-y-2 sm:ml-3">
            {categories.length === 0 ? (
              <p className="text-sm text-slate-500">No policies defined.</p>
            ) : (
              categories.map((category) => {
                const items = resolvedPolicy[category] ?? []
                const all = items.length > 0 && items.every((p) => checkedPolicies.has(String(p.policy_key)))
                const expanded = expandedPolicy === category
                return (
                  <div
                    key={category}
                    className="form-group border-b border-slate-100 pb-2 pt-1 last:border-0"
                  >
                    <div className="flex items-start gap-1">
                      <button
                        type="button"
                        onClick={() => togglePolicyGroup(category)}
                        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center text-[#232946] hover:text-[rgb(21_151_255)]"
                        aria-expanded={expanded}
                        aria-label={expanded ? 'Collapse policies' : 'Expand policies'}
                      >
                        {expanded ? (
                          <Minus className="h-3.5 w-3.5" strokeWidth={2.5} />
                        ) : (
                          <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                        )}
                      </button>
                      <input
                        id={`cat-${category}`}
                        type="checkbox"
                        className={L.check}
                        checked={all}
                        disabled={formLocked}
                        onChange={(e) => toggleCategory(category, e.target.checked)}
                      />
                      <label
                        htmlFor={`cat-${category}`}
                        className="min-w-0 flex-1 pt-0.5 text-sm font-medium leading-snug text-[#232946] [word-break:break-word]"
                      >
                        {category}
                      </label>
                    </div>
                    {expanded && items.length > 0 && (
                      <div className="ml-4 mt-2 flex flex-wrap gap-x-4 gap-y-1.5 pl-2 sm:ml-8 sm:pl-2">
                        {items.map((item) => {
                          const key = String(item.policy_key)
                          return (
                            <div
                              key={key}
                              className="flex min-w-0 max-w-full flex-[1_1_45%] items-start gap-2 sm:max-w-[calc(50%-0.5rem)]"
                            >
                              <input
                                id={`pol-${key}`}
                                type="checkbox"
                                className={L.check}
                                checked={checkedPolicies.has(key)}
                                disabled={formLocked}
                                onChange={() => togglePolicy(key)}
                              />
                              <label
                                htmlFor={`pol-${key}`}
                                className="min-w-0 text-sm leading-snug text-[#232946] [word-break:break-word]"
                                title={item.policy_description}
                              >
                                {key}
                              </label>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>

        {changeLog.length > 0 && (
          <div className="ml-0 mt-6 sm:ml-1">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5">
                <h4 className="text-sm font-semibold text-[#232946]">Changes Logs</h4>
              </div>
              <div className="p-3 sm:p-4">
                <div className="overflow-x-auto -mx-1 sm:mx-0">
                  <table className="w-full min-w-full border-collapse text-sm text-[#232946]">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-xs text-slate-600">
                        <th className="p-2 pl-0 sm:pl-1 font-medium">Changes By</th>
                        <th className="w-[62%] p-2 font-medium">Changes Data</th>
                        <th className="p-2 pr-0 sm:pr-1 font-medium">Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changeLog.map((note, idx) => (
                        <tr key={`${idx}-${note.name}`} className="border-b border-slate-100 last:border-0">
                          <td className="align-top p-2 pl-0 sm:pl-1 text-sm">{note.name}</td>
                          <td
                            className="datacontent w-[62%] align-top p-2 text-sm [word-break:break-word]"
                            dangerouslySetInnerHTML={{ __html: note.changes_data ?? '' }}
                          />
                          <td className="align-top p-2 pr-0 sm:pr-1 text-sm">
                            {formatChangeLogCreatedAt(note.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </OnScreenModal>
  )
}
